import { Moon, Sun, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/lib/theme-provider";
import { cn } from "@/lib/utils";

interface ThemeToggleProps {
  variant?: "icon" | "expanded";
  className?: string;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
}

export function ThemeToggle({
  variant = "icon",
  className,
  align = "end",
  side = "top",
}: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();

  // Determine the actual theme being displayed (resolving "system")
  const resolvedTheme =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;

  const themeOptions = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ] as const;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={variant === "icon" ? "icon" : "default"}
          className={cn(
            "relative transition-all duration-200",
            variant === "icon" && [
              "h-9 w-9 rounded-lg",
              "hover:bg-sidebar-accent",
              "text-sidebar-foreground/60 hover:text-sidebar-foreground",
            ],
            variant === "expanded" && [
              "justify-start gap-2",
              "text-muted-foreground hover:text-foreground",
            ],
            className
          )}
        >
          {/* Sun icon - visible in light mode */}
          <Sun
            className={cn(
              "h-5 w-5 transition-all duration-300",
              resolvedTheme === "dark"
                ? "rotate-90 scale-0 opacity-0"
                : "rotate-0 scale-100 opacity-100",
              variant === "icon" && "absolute"
            )}
          />
          {/* Moon icon - visible in dark mode */}
          <Moon
            className={cn(
              "h-5 w-5 transition-all duration-300",
              resolvedTheme === "dark"
                ? "rotate-0 scale-100 opacity-100"
                : "-rotate-90 scale-0 opacity-0",
              variant === "icon" && "absolute"
            )}
          />
          {variant === "expanded" && (
            <span className="ml-1">
              {theme === "system" ? "System" : theme === "dark" ? "Dark" : "Light"}
            </span>
          )}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="min-w-[140px]">
        {themeOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => setTheme(option.value)}
            className={cn(
              "cursor-pointer gap-2",
              theme === option.value && "bg-accent"
            )}
          >
            <option.icon className="h-4 w-4" />
            <span>{option.label}</span>
            {theme === option.value && (
              <span className="ml-auto text-xs text-primary">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Theme preview cards for Settings page
interface ThemeCardProps {
  themeValue: "light" | "dark" | "system";
  currentTheme: string;
  onSelect: (theme: "light" | "dark" | "system") => void;
}

export function ThemeCard({ themeValue, currentTheme, onSelect }: ThemeCardProps) {
  const isActive = currentTheme === themeValue;

  const labels = {
    light: { title: "Light", description: "Clean and bright" },
    dark: { title: "Dark", description: "Easy on the eyes" },
    system: { title: "System", description: "Match your device" },
  };

  const icons = {
    light: Sun,
    dark: Moon,
    system: Monitor,
  };

  const Icon = icons[themeValue];

  return (
    <button
      onClick={() => onSelect(themeValue)}
      className={cn(
        "relative flex flex-col items-center gap-3 rounded-xl border-2 p-6 transition-all duration-200",
        "hover:border-primary/50 hover:bg-accent/50",
        isActive
          ? "border-primary bg-primary/5 shadow-md"
          : "border-border bg-card"
      )}
    >
      {/* Theme preview mockup */}
      <div
        className={cn(
          "w-full aspect-[4/3] rounded-lg overflow-hidden border",
          themeValue === "dark" ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"
        )}
      >
        {/* Mini sidebar */}
        <div className="flex h-full">
          <div
            className={cn(
              "w-1/4 h-full",
              themeValue === "dark" ? "bg-slate-950" : "bg-slate-100"
            )}
          >
            <div className="p-1.5 space-y-1">
              <div
                className={cn(
                  "h-1.5 w-3/4 rounded-full",
                  themeValue === "dark" ? "bg-teal-500" : "bg-teal-600"
                )}
              />
              <div
                className={cn(
                  "h-1 w-1/2 rounded-full",
                  themeValue === "dark" ? "bg-slate-700" : "bg-slate-300"
                )}
              />
              <div
                className={cn(
                  "h-1 w-2/3 rounded-full",
                  themeValue === "dark" ? "bg-slate-700" : "bg-slate-300"
                )}
              />
            </div>
          </div>
          {/* Mini content area */}
          <div className="flex-1 p-1.5">
            <div
              className={cn(
                "h-2 w-1/2 rounded mb-1.5",
                themeValue === "dark" ? "bg-slate-700" : "bg-slate-200"
              )}
            />
            <div className="grid grid-cols-2 gap-1">
              <div
                className={cn(
                  "h-4 rounded",
                  themeValue === "dark" ? "bg-slate-800" : "bg-slate-100"
                )}
              />
              <div
                className={cn(
                  "h-4 rounded",
                  themeValue === "dark" ? "bg-slate-800" : "bg-slate-100"
                )}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="flex items-center gap-2">
        <Icon className={cn("h-4 w-4", isActive ? "text-primary" : "text-muted-foreground")} />
        <div className="text-left">
          <p className={cn("font-medium text-sm", isActive && "text-primary")}>
            {labels[themeValue].title}
          </p>
          <p className="text-xs text-muted-foreground">
            {labels[themeValue].description}
          </p>
        </div>
      </div>

      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
          <span className="text-[10px] text-primary-foreground font-bold">✓</span>
        </div>
      )}
    </button>
  );
}
